import {Component, OnInit} from '@angular/core';
import {WholesalerIntegrationStatusEnum} from './interfaces/WholesalerIntegrationStatusEnum';
import {WholesalerIntegrationStepsEnum} from './interfaces/WholesalerIntegrationStepsEnum';
import {Select, Store} from '@ngxs/store';
import {take, withLatestFrom} from 'rxjs/operators';
import {
  EditWholesalerConnection,
  GetAllWholesalerConnections,
  GetPharmacyManagementSystems,
  SaveWholesalerConnection,
} from '../shared/states/profile/profile.actions';
import {CloseUserAccount, Logout} from '../shared/states/auth/auth.actions';
import {PharmacyLocationEnum} from '../shared/components/pharmacy-details/PharmacyLocationEnum';
import {Observable} from 'rxjs/internal/Observable';
import {IPharmacy} from '../shared/interfaces/pharmacy.interface';
import {PharmacyState} from '../shared/states/pharmacy/pharmacy.state';
import {
  EnableC3toC5Drugs,
  GetUserPharmacies,
  SavePharmacyDetails,
  SavePharmacyForm,
  SavePharmacyNPI,
  UpdatePharmacyDetails,
} from '../shared/states/pharmacy/pharmacy.actions';
import {AuthState} from '../shared/states/auth/auth.state';
import {PharmacyDetailsStepsEnum} from '../dashboard/interfaces/PharmacyDetailsStepsEnum';
import {UserRolesEnum} from '../shared/interfaces/UserRolesEnum';
import {IUser} from '../shared/interfaces/user.interface';
import {ProfileState} from '../shared/states/profile/profile.state';
import {WholesalerIntegrationActivityEnum} from './interfaces/WholesalerIntegrationActivityEnum';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {fadeInOut} from '../shared/animations/animations';
import {BadgeColorsEnum} from '../shared/interfaces/BadgeColorsEnum';
import {IWholesalerConnection} from '../shared/interfaces/wholesaler-integration.interface';
import {PricingOption} from '../shared/interfaces/PricingOption';
import { PharmacyManagementSystemIntegrationStatusEnum } from './interfaces/PharmacyManagementSystemIntegrationStatusEnum';
import { PharmacyManagementSystemIntegrationActivityEnum } from './interfaces/PharmacyManagementSystemIntegrationActivityEnum';
import { PharmacyManagementSystemIntegrationStepsEnum } from './interfaces/PharmacyManagementSystemIntegrationStepsEnum';
import { IIntegrationConfiguration, IntegrationTypeEnum } from '../shared/interfaces/integration.interface';
import { DrugsService } from '../shared/services/drugs.service';
import { SetDashboardFilters } from '../shared/states/transactions/transactions.actions';
import { TransactionsState } from '../shared/states/transactions/transactions.state';
import { PinService } from '../shared/services/pin.service';
import { DrugActivitiesToTrackEnum } from '../shared/interfaces/DrugActivitiesToTrackEnum';
import { IC3C5SettingValue } from '../shared/interfaces/IC3C5SettingValue.interface';
import { PmsStatusEnum } from './interfaces/PmsStatusEnum';

@UntilDestroy()
@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  animations: [fadeInOut],
})
export class SettingsComponent implements OnInit {
  PharmacyManagementSystemIntegrationStatusEnum =
    PharmacyManagementSystemIntegrationStatusEnum;
  PharmacyManagementSystemIntegrationStepsEnum =
    PharmacyManagementSystemIntegrationStepsEnum;
  currentPharmacyManagementSystemIntegrationProgress =
    PharmacyManagementSystemIntegrationStepsEnum.SelectType;

  PharmacyManagementSystemIntegrationActivityEnum =
    PharmacyManagementSystemIntegrationActivityEnum;
  currentPharmacyManagementSystemIntegrationStatus: PharmacyManagementSystemIntegrationStatusEnum =
    PharmacyManagementSystemIntegrationStatusEnum.Initial;

  IntegrationTypeEnum = IntegrationTypeEnum;

  //Initial labels state until an integration type is selected
  pharmacyManagementSystemIntegrationLabels = this.getPmsIntegrationStepperInitialLabel();

  WholesalerIntegrationStatusEnum = WholesalerIntegrationStatusEnum;
  WholesalerIntegrationActivityEnum = WholesalerIntegrationActivityEnum;
  currentWholesalerIntegrationStatus: WholesalerIntegrationStatusEnum =
    WholesalerIntegrationStatusEnum.Initial;

  currentWholesalerIntegrationProgress =
    WholesalerIntegrationStepsEnum.SelectType;
  wholesalerIntegrationLabels = [
    'Gather credentials',
    'Test connection',
    'Finish',
  ];

  createPharmacyProgress: PharmacyDetailsStepsEnum =
    PharmacyDetailsStepsEnum.NpiNumber;
  createPharmacyProgressLabels = ['NPI number', 'Pharmacy details'];

  pharmacyManagementRoles = [
    UserRolesEnum.GlobalAdmin,
    UserRolesEnum.CompanyAdmin,
  ];

  showEditPharmacyDetails: boolean;

  showWholesalerList = true;
  showPharmacyManagementSystemList = true;

  showEditProfileDialog: boolean;
  showPharmacyManagementSystemIntegrationDialog: boolean;
  showWholesalerIntegrationDialog: boolean;
  showCreatePharmacyDialog: boolean;
  showCloseAccountDialog: boolean;
  isNotBasicPlan: boolean;

  UserRolesEnum = UserRolesEnum;
  PharmacyLocationEnum = PharmacyLocationEnum;

  currentWholesaler: IWholesalerConnection;
  currentPharmacyId: string;

  currentWITab = WholesalerIntegrationActivityEnum.Active;

  pharmacyManagementSystemIntegrationList: IIntegrationConfiguration[] = [];
  selectedPharmacyManagementSystemIntegration: IntegrationTypeEnum;

  wholesalerIntegrationActiveList: IWholesalerConnection[] = [];
  wholesalerIntegrationInactiveList: IWholesalerConnection[] = [];

  BadgeColorsEnum = BadgeColorsEnum;

  @Select(AuthState.currentUser) user$: Observable<IUser>;
  @Select(PharmacyState.currentPharmacy)
  currentPharmacy$: Observable<IPharmacy>;
  @Select(ProfileState.loading) loading$: Observable<boolean>;
  @Select(PharmacyState.pharmaciesICanEdit) pharmacies$: Observable<
    IPharmacy[]
  >;
  @Select(ProfileState.wholesalerIntegrations)
  wholesalerIntegrations$: Observable<IWholesalerConnection[]>;
  @Select(PharmacyState.success) successPharmacy$: Observable<boolean>;
  @Select(AuthState.success) successAuth$: Observable<boolean>;
  @Select(ProfileState.pharmacyManagementSystemIntegrations)
  pmsIntegrations$: Observable<IIntegrationConfiguration[]>;

  @Select(PharmacyState.enableC3toC5Drugs) enableC3toC5Drugs$: Observable<boolean>;
  @Select(PharmacyState.enableC3toC5DrugsValue) enableC3toC5DrugsValue$: Observable<IC3C5SettingValue>;

  DrugActivitiesToTrackEnum = DrugActivitiesToTrackEnum;

  setupDisabled = false;

  enableC3toC5Drugs: boolean;
  runEnableC3C5Workflow: boolean;

  drugsOfInterest: IC3C5SettingValue;

  enableC3toC5DrugsPinChannel = 'enableC3toC5Drugs';

  constructor(private store: Store, private pinService: PinService) {}

  ngOnInit(): void {
    this.clearPharmacyFormInput();

    const pricingOption =
      this.store.selectSnapshot(PharmacyState)?.currentPharmacy?.pricingOption;
    this.isNotBasicPlan =
      pricingOption &&
      pricingOption !== PricingOption.monthlyBasic &&
      pricingOption !== PricingOption.annualBasic;

    if (this.isNotBasicPlan) {
      this.store.dispatch(new GetAllWholesalerConnections());
      this.store.dispatch(new GetPharmacyManagementSystems());

      this.wholesalerIntegrations$
        .pipe(untilDestroyed(this))
        .subscribe((data: IWholesalerConnection[] = []) => {
          this.wholesalerIntegrationActiveList = [
            ...data.filter((el) => el.isActive),
          ];
          this.wholesalerIntegrationInactiveList = [
            ...data.filter((el) => !el.isActive),
          ];
        });

      this.pmsIntegrations$
        .pipe(untilDestroyed(this))
        .subscribe((data: IIntegrationConfiguration[] = []) => {
          this.setNextPmsStep(PharmacyManagementSystemIntegrationStepsEnum.SelectType);
          this.setupDisabled = false;          
        });
    }
  }

  openEditProfileDialog(): void {
    this.showEditProfileDialog = true;
  }

  afterEditProfile(success: boolean): void {
    // TODO: show message about saved status
    this.closeEditProfileDialog();
  }

  closeEditProfileDialog(): void {
    this.showEditProfileDialog = false;
  }

  createPharmacyStepSelected(step: PharmacyDetailsStepsEnum): void {
    this.createPharmacyProgress = step;
  }

  clearPharmacyFormInput(): void {
    this.store.dispatch(new SavePharmacyNPI(null));
    this.store.dispatch(new SavePharmacyForm(null));
  }

  openAddPharmacyDialog(): void {
    this.currentPharmacyId = null;
    this.clearPharmacyFormInput();
    this.setCreatePharmacyStep(PharmacyDetailsStepsEnum.NpiNumber);
    this.setFocus('app-npi-number input');
    this.showCreatePharmacyDialog = true;
  }

  openEditPharmacyDialog(id: string): void {
    this.currentPharmacyId = id;
    this.showEditPharmacyDetails = true;
  }

  closeCreatePharmacy(): void {
    this.showCreatePharmacyDialog = false;
  }

  closeEditPharmacy(): void {
    this.showEditPharmacyDetails = false;
  }

  setCreatePharmacyStep(step: PharmacyDetailsStepsEnum): void {
    this.createPharmacyProgress = step;
  }

  savePharmacyDetails(formValue: any): void {
    this.store
      .dispatch(new SavePharmacyForm(formValue))
      .pipe(take(1))
      .subscribe((data: any) => {
        if (data) {
          this.store
            .dispatch(new SavePharmacyDetails())
            .pipe(take(1), withLatestFrom(this.successPharmacy$))
            .subscribe(([_, saved]) => {
              if (saved) {
                this.store.dispatch(new GetUserPharmacies());
                this.store.dispatch(new SavePharmacyForm(null));
                this.closeCreatePharmacy();
              }
            });
        }
      });
  }

  editPharmacyDetails(formValue: any): void {
    this.store
      .dispatch(new UpdatePharmacyDetails(formValue))
      .pipe(take(1), withLatestFrom(this.successPharmacy$))
      .subscribe(([_, updated]) => {
        if (updated) {
          this.closeEditPharmacy();
        }
      });
  }

  tabWIChanged(tab: WholesalerIntegrationActivityEnum): void {
    this.currentWITab = tab;
  }

  openPharmacyManagementSystemIntegrationDialog(): void {
    this.showPharmacyManagementSystemIntegrationDialog = true;
  }

  openSecondStepDialog(val): void {
    this.selectedPharmacyManagementSystemIntegration = IntegrationTypeEnum.LibertyRx;
    this.setStepperLabels(IntegrationTypeEnum.LibertyRx);
    this.currentPharmacyManagementSystemIntegrationProgress = PharmacyManagementSystemIntegrationStepsEnum.PendingConnection;
    this.showPharmacyManagementSystemIntegrationDialog = true;
  }

  closePharmacyManagementSystemIntegrationDialog(): void {
    this.currentPharmacyManagementSystemIntegrationProgress =
      PharmacyManagementSystemIntegrationStepsEnum.SelectType;
    this.showPharmacyManagementSystemIntegrationDialog = false;
    this.selectedPharmacyManagementSystemIntegration = null;
    this.pharmacyManagementSystemIntegrationLabels = this.getPmsIntegrationStepperInitialLabel();
    this.store.dispatch(new GetPharmacyManagementSystems());
  }

  openWholesalerIntegrationDialog(item?: IWholesalerConnection): void {
    this.currentWholesaler = item;
    this.showWholesalerIntegrationDialog = true;
  }

  saveWholesalerIntegration(data: IWholesalerConnection): void {
    if (data.id) {
      this.store
        .dispatch(new EditWholesalerConnection(data))
        .pipe(take(1))
        .subscribe(() => {
          this.closeWholesalerIntegrationDialog();
        });
    } else {
      this.store
        .dispatch(new SaveWholesalerConnection(data))
        .pipe(take(1))
        .subscribe(() => {
          this.closeWholesalerIntegrationDialog();
        });
    }
  }

  setNextPmsStep(step: PharmacyManagementSystemIntegrationStepsEnum): void {
    this.currentPharmacyManagementSystemIntegrationProgress = step;
  }

  setNextStep(step: WholesalerIntegrationStepsEnum): void {
    this.currentWholesalerIntegrationProgress = step;
  }

  closeWholesalerIntegrationDialog(): void {
    this.currentWholesalerIntegrationProgress =
      WholesalerIntegrationStepsEnum.SelectType;
    this.showWholesalerIntegrationDialog = false;
    this.currentWholesaler = null;
  }

  openCloseAccountDialog(): void {
    this.showCloseAccountDialog = true;
  }

  closeAccount(): void {
    this.store
      .dispatch(new CloseUserAccount())
      .pipe(take(1), withLatestFrom(this.successAuth$))
      .subscribe(([_, closed]) => {
        if (closed) {
          this.closeCloseAccountDialog();
          this.store.dispatch(new Logout());
        }
      });
  }

  closeCloseAccountDialog(): void {
    this.showCloseAccountDialog = false;
  }

  setFocus(selector: string): void {
    setTimeout(() => {
      const inputEl = document.querySelector(selector) as HTMLInputElement;
      if (inputEl) {
        inputEl.blur();
        inputEl.focus();
      }
    }, 0);
  }


  setStepperLabels(type: IntegrationTypeEnum): void {
    if (type in IntegrationTypeEnum) {
      this.pharmacyManagementSystemIntegrationLabels = this.getPmsIntegrationStepperInitialLabel();
      switch (type) {
        case IntegrationTypeEnum.PioneerRx:
          this.pharmacyManagementSystemIntegrationLabels.push('Sign consent form');
          break;
        case IntegrationTypeEnum.FrameworkLTC:
          this.pharmacyManagementSystemIntegrationLabels.push('Confirm Selection');
          break;
        default:
          this.pharmacyManagementSystemIntegrationLabels.push('Test connection');
          this.pharmacyManagementSystemIntegrationLabels.push('Finish');
          break;
      }
    }
    
  }

  private getPmsIntegrationStepperInitialLabel(): string[] {
    return ['Select system'];
  }
}